import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';

const SUGGESTIONS = [
  'Why did the last pipeline fail?',
  'Show recent deployments to production',
  'Which automation rules are active?',
  'Why is hr-cap-project slow?',
  'Show errors in hr-cap-project-srv logs',
  'Which apps are stopped right now?',
];

const renderMarkdown = (text) => text.split('\n').map((line, i) => {
  if (line.startsWith('## ') || line.startsWith('### '))
    return <div key={i} style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', margin: '8px 0 3px' }}>{line.replace(/^#+\s/, '')}</div>;
  if (line.startsWith('- ') || line.startsWith('* '))
    return <div key={i} style={{ paddingLeft: 12, marginBottom: 2, fontSize: 13 }}>• {line.replace(/^[-*]\s/, '')}</div>;
  if (line.trim() === '') return <div key={i} style={{ height: 5 }} />;
  return <div key={i} style={{ fontSize: 13, marginBottom: 2 }}>{line}</div>;
});

export default function GlobalChatBot() {
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState([
    { role: 'assistant', text: 'Hi! I\'m Yuvi 👋 Ask me anything about your pipelines, deployments, CF apps, automation rules, or projects. I\'ll fetch live data to answer!' }
  ]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [projects, setProjects]   = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [unread, setUnread]       = useState(0);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (!open && messages[messages.length - 1]?.role === 'assistant' && messages.length > 1) {
      setUnread(u => u + 1);
    }
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post('/assistant/chat', {
        message: msg,
        projectId: selectedProject || null,
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: data.answer,
        meta: [
          data.detectedProject !== 'Not identified' ? data.detectedProject : null,
          ...(data.contextFetched || [])
        ].filter(Boolean).join(' · ') || null,
        pendingAction: data.pendingAction || null,
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: '⚠️ ' + (e.response?.data?.error || e.message),
        error: true,
      }]);
    }
    setLoading(false);
  };

  const confirmAction = async (action) => {
    setMessages(prev => prev.map(m => m.pendingAction === action ? { ...m, pendingAction: null } : m));
    setMessages(prev => [...prev, { role: 'user', text: `Yes, ${action.actionType.toLowerCase()} them.` }]);
    setLoading(true);
    try {
      const { data } = await api.post('/assistant/execute', action);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: data.summary + '\n\n' + data.result,
        meta: action.projectName,
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: '⚠️ ' + (e.response?.data?.error || e.message),
        error: true,
      }]);
    }
    setLoading(false);
  };

  const cancelAction = (action) => {
    setMessages(prev => prev.map(m => m.pendingAction === action ? { ...m, pendingAction: null } : m));
    setMessages(prev => [...prev, { role: 'assistant', text: 'No problem! Action cancelled. Let me know if you need anything else.' }]);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Ask Yuvi"
        style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 1000,
          width: 52, height: 52, borderRadius: '50%',
          background: open ? '#0f172a' : 'linear-gradient(135deg, #1a56db, #0ea5e9)',
          color: '#fff', border: 'none', cursor: 'pointer', fontSize: 22,
          boxShadow: '0 4px 16px rgba(26,86,219,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}>
        {open ? '✕' : '🤖'}
        {!open && unread > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: 0,
            background: '#dc2626', color: '#fff', borderRadius: '50%',
            width: 18, height: 18, fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{unread}</span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 90, right: 28, zIndex: 999,
          width: 400, height: 560, borderRadius: 16,
          background: '#fff', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px', background: 'linear-gradient(135deg, #1a56db, #0ea5e9)',
            color: '#fff', display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>🤖</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Yuvi</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>Your DevOps Assistant · Pipelines · CF Apps · Rules</div>
            </div>
            {/* Project selector */}
            <select
              value={selectedProject}
              onChange={e => setSelectedProject(e.target.value)}
              style={{
                fontSize: 11, padding: '3px 6px', borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.4)',
                background: 'rgba(255,255,255,0.15)', color: '#fff', maxWidth: 130,
              }}>
              <option value="" style={{ color: '#000' }}>Auto-detect</option>
              {projects.filter(p => p.cfAppName).map(p => (
                <option key={p.id} value={p.id} style={{ color: '#000' }}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '12px 14px',
            display: 'flex', flexDirection: 'column', gap: 10,
            background: '#f8fafc',
          }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '85%', padding: '10px 13px',
                  borderRadius: m.role === 'user' ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
                  background: m.role === 'user' ? '#1a56db' : '#fff',
                  color: m.role === 'user' ? '#fff' : m.error ? '#dc2626' : '#0f172a',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                  border: m.role === 'assistant' ? '1px solid #e2e8f0' : 'none',
                  fontSize: 13,
                }}>
                  {m.role === 'assistant' && m.meta && (
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 5, fontWeight: 500 }}>
                      📍 {m.meta}
                    </div>
                  )}
                  {m.role === 'user'
                    ? <span>{m.text}</span>
                    : <div>{renderMarkdown(m.text)}</div>
                  }
                  {m.pendingAction && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button onClick={() => confirmAction(m.pendingAction)} style={{
                        padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        background: m.pendingAction.actionType === 'START' ? '#16a34a' : '#dc2626',
                        color: '#fff', fontWeight: 600, fontSize: 13,
                      }}>
                        {m.pendingAction.actionType === 'START' ? '▶ Yes, Start' : '⏹ Yes, Stop'}
                      </button>
                      <button onClick={() => cancelAction(m.pendingAction)} style={{
                        padding: '7px 18px', borderRadius: 8, border: '1px solid #e2e8f0',
                        cursor: 'pointer', background: '#f8fafc', color: '#64748b', fontSize: 13,
                      }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '10px 14px', background: '#fff', borderRadius: '14px 14px 14px 3px',
                  border: '1px solid #e2e8f0', fontSize: 12, color: '#64748b',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span>Yuvi is thinking...</span>
                  <span style={{ display: 'flex', gap: 3 }}>
                    {[0,1,2].map(i => (
                      <span key={i} style={{
                        width: 6, height: 6, borderRadius: '50%', background: '#1a56db',
                        animation: `bounce 1.2s ${i * 0.2}s infinite`,
                        display: 'inline-block',
                      }} />
                    ))}
                  </span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div style={{ padding: '8px 12px', display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: '1px solid #f1f5f9' }}>
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => send(s)} style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 14,
                  border: '1px solid #e2e8f0', background: '#f8fafc',
                  color: '#374151', cursor: 'pointer', lineHeight: 1.4,
                }}>{s}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: '10px 12px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder="Ask Yuvi anything..."
              disabled={loading}
              style={{
                flex: 1, padding: '9px 13px', fontSize: 13, borderRadius: 8,
                border: '1px solid #e2e8f0', outline: 'none',
                background: loading ? '#f8fafc' : '#fff',
              }}
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              style={{
                padding: '9px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: loading || !input.trim() ? '#e2e8f0' : '#1a56db',
                color: loading || !input.trim() ? '#94a3b8' : '#fff',
                fontSize: 16, transition: 'all 0.15s',
              }}>➤</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </>
  );
}
